import { Base2Presenter } from "core/presenter";
import { CompanyRepository } from "core/repository";
import { CompanyConstraint } from "core/constraint";
import * as R from "ramda";
import _ from "lodash";
import { fn, validate } from "util";

class AccountantAreaPresenter extends Base2Presenter {
  initialState() {
    return {
      prevCompany: {
        default_taxes: {
          csosn: "",
          cfop: "",
          pis: "",
          cofins: "",
          pis_normal: {
            cst: "",
            pPIS: "0",
            vAliqProd: "0"
          },
          cofins_normal: {
            cst: "",
            pCOFINS: "0",
            vAliqProd: "0"
          },
          icms: {
            cst: "",
            cfop: "",
            modBC: "",
            modBCST: "",
            motDesICMS: "",
            pFCP: "0.00",
            pFCPST: "0",
            pICMS: "0",
            pICMSST: "0",
            pMVAST: "0",
            pRedBCST: "0",
            pRedBC: "0",
            pST: "0",
            pFCPSTRet: "0",
            pRedBCEfet: "0",
            pICMSEfet: "0"
          },
          origin: ""
        }
      },
      company: {
        fiscal_informations: {
          certificate_password: "",
          certificate: ""
        },
        default_taxes: {
          csosn: "",
          cfop: "",
          pis: "",
          cofins: "",
          pis_normal: {
            cst: "",
            pPIS: "0",
            vAliqProd: "0"
          },
          cofins_normal: {
            cst: "",
            pCOFINS: "0",
            vAliqProd: "0"
          },
          icms: {
            cst: "",
            cfop: "",
            modBC: "",
            modBCST: "",
            motDesICMS: "",
            pFCP: "0.00",
            pFCPST: "0",
            pICMS: "0",
            pICMSST: "0",
            pMVAST: "0",
            pRedBCST: "0",
            pRedBC: "0",
            pST: "0",
            pFCPSTRet: "0",
            pRedBCEfet: "0",
            pICMSEfet: "0"
          },
          origin: ""
        },
        certificate_info: {}
      },
      loading: false,
      errors: {}
    };
  }

  async onStart() {
    this.currentUser = await this.mRuntime.get("currentUser");
    this.getCompany();
  }

  async getCompany() {
    const companyResult = await CompanyRepository.getOnline(
      this.currentUser.company_uuid
    );

    if (companyResult.success) {
      const defaultTaxesResult = companyResult.data.default_taxes || {};
      const defaultFiscalInformationsResult =
        companyResult.data.fiscal_informations || {};
      defaultFiscalInformationsResult.certificate =
        defaultFiscalInformationsResult.certificate || "";
      defaultFiscalInformationsResult.certificate_password =
        defaultFiscalInformationsResult.certificate_password || "";

      this.enqueue({
        company: {
          fiscal_informations: defaultFiscalInformationsResult,
          default_taxes: {
            ...this.viewModel.company.default_taxes,
            ...defaultTaxesResult
          },
          certificate_info: companyResult.data.certificate_info || {}
        },
        prevCompany: {
          default_taxes: {
            ...this.viewModel.company.default_taxes,
            ...defaultTaxesResult
          }
        }
      });
    }
  }

  async onSendForm() {
    this.enqueue({ loading: true });

    const company = this.viewModel.company;
    const taxes = ["cfop", "csosn", "pis", "cofins", "origin"];

    taxes.forEach(tax => {
      if (fn.isBlank(company.default_taxes[tax])) {
        delete company.default_taxes[tax];
      }
    });

    ["certificate", "certificate_password"].forEach(field => {
      if (fn.isBlank(company.fiscal_informations[field])) {
        delete company.fiscal_informations[field];
      }
    });

    const isValid = await this.doValidateForm();

    if (company.fiscal_informations.crt_code !== "3") {
      delete company.default_taxes.pis_normal;
      delete company.default_taxes.cofins_normal;
      delete company.default_taxes.icms;
    } else {
      delete company.default_taxes.pis;
      delete company.default_taxes.cofins;
    }

    if (isValid) {
      const create = await CompanyRepository.update(
        this.currentUser.company_uuid,
        company
      );
      this.postPersist(create);
    } else {
      this.postPersist({
        success: false,
        error:
          "Ocorreu um erro ao atualizar a Configuração Geral. Verifique as informações tributárias."
      });
    }
    this.enqueue({ loading: false });
  }

  async postPersist(response) {
    if (!response.success) {
      this.sendNotification({
        type: "error",
        title: "Oops...",
        content: this.defineErrorMessage(response)
      });

      return;
    }

    await this.mRuntime.set("currentUser", {
      ...this.currentUser,
      company: { ...this.currentUser.company, ...response.data }
    });

    this.sendNotification({
      type: "success",
      content: `A configuração geral da empresa ${this.currentUser.company.slug} foi atualizada.`
    });
  }

  defineErrorMessage(response) {
    const errors = response.errors || {};
    const defaultMessage =
      "Ocorreu um erro ao atualizar as informações na Invoicy. Tente novamente mais tarde.";

    if (errors && (errors.code === "173" || errors.code === "101")) {
      return "Prezado(a), é possível que sua empresa já esteja cadastrada na Invoicy, o que impede um novo cadastro pela PDVend, favor entrar em contato com o suporte da Invoicy para substituição do seu cadastro.";
    }

    return response.error || defaultMessage;
  }

  onChange(field, subField, value) {
    this.enqueue(vm => ({
      company: {
        ...vm.company,
        [field]: {
          ...vm.company[field],
          [subField]: value
        }
      }
    }));
    if (subField === "crt_code") {
      this.clearAllInformation();
    }
  }

  async setFieldDefault(field, cst) {
    const fieldsRules = {
      pis_normal: {
        pPIS: ["01", "02", "49", "99"],
        vAliqProd: ["03"]
      },
      cofins_normal: {
        pCOFINS: ["01", "02", "49"],
        vAliqProd: ["03"]
      },
      icms: {
        modBC: ["00", "10", "20"],
        modBCST: ["10", "30"],
        motDesICMS: ["20", "30"],
        pFCP: ["00", "10", "20"],
        pFCPST: ["10", "30"],
        pICMS: ["00", "10", "20"],
        pICMSST: ["10", "30"],
        pMVAST: ["10", "30"],
        pRedBCST: ["10", "30"],
        pRedBC: ["20"],
        pST: ["60"],
        pFCPSTRet: ["60"],
        pRedBCEfet: ["60"],
        pICMSEfet: ["60"]
      }
    };

    const setZero = {
      pis_normal: ["pPIS", "vAliqProd"],
      cofins_normal: ["pCOFINS", "vAliqProd"],
      icms: [
        "pFCP",
        "pFCPST",
        "pICMS",
        "pICMSST",
        "pMVAST",
        "pRedBCST",
        "pRedBC",
        "pST",
        "pFCPSTRet",
        "pRedBCEfet",
        "pICMSEfet"
      ]
    };

    const sharedObject = {};

    const prev = this.viewModel.prevCompany.default_taxes;
    await Promise.all(
      _.map(fieldsRules[field], (e, k) => {
        if (e.includes(prev[field]["cst"]) && e.includes(cst)) {
          sharedObject[k] = prev[field][k];
        }
      })
    );

    await Promise.all(
      _.map(setZero[field], e => {
        if (
          _.isUndefined(sharedObject[e]) &&
          fieldsRules[field][e].includes(cst)
        ) {
          sharedObject[e] = e === "pFCP" ? "0.00" : "0";
        }
      })
    );

    if (field === "icms") {
      sharedObject["cfop"] = null;
    }

    this.enqueue(vm => ({
      company: {
        ...vm.company,
        default_taxes: {
          ...vm.company.default_taxes,
          [field]: {
            ...sharedObject,
            cst
          }
        }
      }
    }));
  }

  onChangeDefaultTaxes(field, subField, value) {
    this.enqueue(vm => ({
      company: {
        ...vm.company,
        default_taxes: {
          ...vm.company.default_taxes,
          [field]: {
            ...vm.company.default_taxes[field],
            [subField]: value
          }
        }
      }
    }));

    if (subField === "cst") {
      if (this.viewModel.prevCompany.default_taxes[field][subField] === value) {
        this.enqueue(vm => ({
          company: {
            ...vm.company,
            default_taxes: {
              ...vm.company.default_taxes,
              [field]: {
                ...vm.prevCompany.default_taxes[field]
              }
            }
          }
        }));
      } else {
        this.setFieldDefault(field, value);
      }
    }
  }

  setPisCofinsToSeven() {
    this.enqueue(vm => ({
      company: {
        ...vm.company,
        default_taxes: {
          ...vm.company.default_taxes,
          pis: "07",
          cofins: "07"
        }
      }
    }));
  }

  clearFiscalInformations() {
    this.enqueue(vm => ({
      company: {
        ...vm.company,
        fiscal_informations: {
          ...vm.company.fiscal_informations,
          picms: "",
          modbc: "",
          predbc: "",
          vicmsdeson: "",
          motdesicms: "",
          vbcstret: "",
          vicmsstret: ""
        }
      }
    }));
  }

  clearAllInformation() {
    this.enqueue(vm => ({
      company: {
        ...vm.company,
        default_taxes: {
          ...vm.company.default_taxes,
          cfop: "",
          csosn: "",
          pis: "",
          cofins: "",
          pis_normal: {
            cst: "",
            pPIS: "0",
            vAliqProd: "0"
          },
          cofins_normal: {
            cst: "",
            pCOFINS: "0",
            vAliqProd: "0"
          },
          icms: {
            cst: "",
            cfop: null,
            modBC: "",
            modBCST: "",
            motDesICMS: "",
            pFCP: "0.00",
            pFCPST: "0",
            pICMS: "0",
            pICMSST: "0",
            pMVAST: "0",
            pRedBCST: "0",
            pRedBC: "0",
            pST: "0",
            pFCPSTRet: "0",
            pRedBCEfet: "0",
            pICMSEfet: "0"
          },
          origin: "",
          cst: ""
        },
        fiscal_informations: {
          ...vm.company.fiscal_informations,
          picms: "",
          modbc: "",
          predbc: "",
          vicmsdeson: "",
          motdesicms: "",
          vbcstret: "",
          vicmsstret: ""
        }
      }
    }));
  }

  async doValidateForm() {
    const validation = validate(
      this.viewModel.company,
      CompanyConstraint.taxes,
      { fullMessages: false }
    );

    if (!!validation) {
      this.viewModel.errors = R.mapObjIndexed(
        errors => errors.join(", "),
        validation
      );
    } else {
      this.viewModel.errors = {};
    }

    return Object.keys(this.viewModel.errors).length === 0;
  }
}

export default AccountantAreaPresenter;



// WEBPACK FOOTER //
// ./src/components/app/accountant-area/presenter.js